import { useState, type ReactNode } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { PermissionKey } from '@pm/contracts';
import { useAccess } from '../access.js';
import { ForecastAgent } from './ForecastAgent.js';
import { GlobalSearch } from './GlobalSearch.js';
import { ContextualHelp } from './ContextualHelp.js';
import { actionPermissions } from './navigationPermissions.js';
import { publicConfig } from '../config.js';
import { SquadNavigation } from './SquadNavigation.js';

const navigation = [
  {
    label: 'Accueil',
    links: [
      ['/', 'dashboard', 'budgets.read'],
      ['/actions', 'myActions', 'portfolio.projects.read'],
    ],
  },
  {
    label: 'Production',
    links: [
      ['/projects', 'projects', 'portfolio.projects.read'],
      ['/planning', 'planningTriage', 'planning.read'],
      ['/capacity', 'capacity', 'capacity.read'],
      ['/resources', 'resources', 'capacity.read'],
    ],
  },
  {
    label: 'Finance',
    links: [
      ['/budgets', 'budgets', 'budgets.read'],
      ['/billing', 'billing', 'billing.read'],
      ['/financial-forecast', 'financialForecast', 'financial.rates.read'],
    ],
  },
  {
    label: 'Pilotage',
    links: [
      ['/insights', 'insights', 'portfolio.projects.read'],
      ['/governance', 'governance', 'governance.read'],
      ['/reports', 'reports', 'reports.read'],
    ],
  },
] as const;

// These views remain available to users with the relevant permission through
// their direct URLs, but are intentionally kept out of the primary navigation.
const hiddenNavigationPaths = new Set(['/planning', '/governance', '/reports']);

function sectionForPath(pathname: string) {
  if (pathname === '/' || pathname.startsWith('/actions')) return 'Accueil';
  if (
    pathname.startsWith('/projects') ||
    pathname.startsWith('/planning') ||
    pathname.startsWith('/capacity') ||
    pathname.startsWith('/resources') ||
    pathname.startsWith('/production/squads')
  )
    return 'Production';
  if (
    pathname.startsWith('/budgets') ||
    pathname.startsWith('/billing') ||
    pathname.startsWith('/financial-forecast')
  )
    return 'Finance';
  if (
    pathname.startsWith('/insights') ||
    pathname.startsWith('/governance') ||
    pathname.startsWith('/reports')
  )
    return 'Pilotage';
  if (
    pathname.startsWith('/settings') ||
    pathname.startsWith('/data-quality') ||
    pathname.startsWith('/integrations')
  )
    return 'Données et configuration';
  if (pathname.startsWith('/admin')) return 'Administration';
  return null;
}

function SidebarSection({
  id,
  label,
  open,
  active,
  onToggle,
  children,
}: {
  id: string;
  label: string;
  open: boolean;
  active: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  return (
    <div className={`sidebar-nav-group${open ? ' is-open' : ''}${active ? ' is-active' : ''}`}>
      <button
        className="sidebar-nav-group-toggle"
        type="button"
        aria-expanded={open}
        aria-controls={id}
        onClick={onToggle}
      >
        <span>{label}</span>
        <span className="sidebar-nav-chevron" aria-hidden="true" />
      </button>
      <div className="sidebar-nav-links" id={id} hidden={!open}>
        {children}
      </div>
    </div>
  );
}

export function Layout({
  user,
  features,
}: {
  user: { displayName: string };
  features: { capacity: boolean; insights: boolean; finance: boolean; agent: boolean };
}) {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const access = useAccess();
  const activeSection = sectionForPath(location.pathname);
  const [navigationState, setNavigationState] = useState(() => ({
    pathname: location.pathname,
    sections: new Set(activeSection ? [activeSection] : []),
  }));
  const openSections =
    navigationState.pathname === location.pathname
      ? navigationState.sections
      : new Set([...navigationState.sections, ...(activeSection ? [activeSection] : [])]);
  const toggleSection = (section: string) => {
    setNavigationState(() => {
      const next = new Set(openSections);
      if (next.has(section)) next.delete(section);
      else next.add(section);
      return { pathname: location.pathname, sections: next };
    });
  };
  const canAdmin =
    access.hasGlobal('admin.users.read') ||
    access.hasGlobal('admin.roles.read') ||
    access.hasGlobal('admin.teams.read');
  const canSeeSettings = access.hasGlobal('settings.read');
  const canSeeRates =
    features.finance &&
    (access.hasAny('financial.rates.read') || access.hasGlobal('financial.rates.manage'));
  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="wordmark">novatize</div>
        <div className="product-name">PULSE</div>
        <nav aria-label={t('shell.mainNavigation')}>
          {navigation.map((section) => {
            const links = section.links.filter(
              ([path, _key, permission]) =>
                !hiddenNavigationPaths.has(path) &&
                ((path !== '/capacity' && path !== '/resources') || features.capacity) &&
                (path !== '/insights' || features.insights) &&
                (path === '/actions'
                  ? actionPermissions.some((actionPermission) => access.hasAny(actionPermission))
                  : access.hasAny(permission as PermissionKey)),
            );
            return (
              <SidebarSection
                key={section.label}
                id={`sidebar-section-${section.label.toLowerCase()}`}
                label={section.label}
                open={openSections.has(section.label)}
                active={activeSection === section.label}
                onToggle={() => toggleSection(section.label)}
              >
                {links.length === 0 && <p className="sidebar-nav-empty">Aucune vue accessible.</p>}
                {links.map(([path, key]) => (
                  <NavLink key={path} to={path} end={path === '/'}>
                    {t(key)}
                  </NavLink>
                ))}
                {section.label === 'Production' && access.hasAny('capacity.read') && (
                  <SquadNavigation />
                )}
              </SidebarSection>
            );
          })}
          <SidebarSection
            id="sidebar-section-settings"
            label="Données et configuration"
            open={openSections.has('Données et configuration')}
            active={activeSection === 'Données et configuration'}
            onToggle={() => toggleSection('Données et configuration')}
          >
            {!canSeeSettings &&
              !canSeeRates &&
              !access.hasGlobal('integrations.read') &&
              !access.hasAny('data.quality.read') &&
              !access.hasGlobal('settings.manage') &&
              !access.hasAny('capacity.read') && (
                <p className="sidebar-nav-empty">Aucune vue accessible.</p>
              )}
            {canSeeSettings && <NavLink to="/settings/clients">{t('tempoTracking')}</NavLink>}
            {(access.hasAny('capacity.read') || access.hasGlobal('settings.manage')) && (
              <NavLink to="/settings/squads">Squads</NavLink>
            )}
            {access.hasAny('data.quality.read') && (
              <NavLink to="/data-quality">{t('dataQuality')}</NavLink>
            )}
            {access.hasGlobal('integrations.read') && (
              <NavLink to="/integrations">{t('integrations')}</NavLink>
            )}
            {canSeeSettings && (
              <NavLink to="/settings/contracts">Configuration des alertes</NavLink>
            )}
            {canSeeSettings && <NavLink to="/settings/forecast">{t('settingsForecast')}</NavLink>}
            {access.hasGlobal('settings.manage') && (
              <NavLink to="/settings/budget-imports">{t('settingsBudgetImports')}</NavLink>
            )}
            {canSeeRates && <NavLink to="/settings/rates">{t('settingsRates')}</NavLink>}
          </SidebarSection>
          <SidebarSection
            id="sidebar-section-admin"
            label={t('accessAdmin.navigation')}
            open={openSections.has('Administration')}
            active={activeSection === 'Administration'}
            onToggle={() => toggleSection('Administration')}
          >
            {canAdmin ? (
              <NavLink to="/admin/access">{t('accessAdmin.title')}</NavLink>
            ) : (
              <p className="sidebar-nav-empty">Aucune vue accessible.</p>
            )}
          </SidebarSection>
        </nav>
        <div className="sidebar-footer">
          <span>{user.displayName}</span>
          <small>{access.roleNames.join(' · ') || t('accessAdmin.pending')}</small>
          {publicConfig.experimentalEnglish && (
            <button
              className="language-button"
              type="button"
              title={
                i18n.language === 'fr'
                  ? t('shell.englishExperimentalTitle')
                  : t('shell.frenchReferenceTitle')
              }
              onClick={() => void i18n.changeLanguage(i18n.language === 'fr' ? 'en' : 'fr')}
            >
              {i18n.language === 'fr' ? t('shell.english') : t('shell.french')}
            </button>
          )}
          <ContextualHelp />
        </div>
      </aside>
      <main className="main-content">
        <GlobalSearch />
        <Outlet />
      </main>
      {features.agent && access.hasAny('agent.read') && <ForecastAgent />}
    </div>
  );
}
